import { useState, useEffect, useCallback, useRef } from 'react';
import { waitForAuthReady, isAuthReady } from '../auth/authReadiness';
import { authenticatedFetch } from '../utils/api';
import { API_URLS } from '../constants';
import { useLanguage } from '@/contexts';

// It is recommended to move these interfaces to a shared types file in 'frontend/src/types'
export interface Prompt {
  id: string;
  category_id: number;
  created_at: string;
  description: string;
  full_text: string;
  is_public: boolean;
  model_type: string;
  modified_at: string;
  posted_at: string;
  title: string;
  updated_at: string;
  user_id: string;
  views: number;
  votes: number;
  systemprompt?: string;
  total_views: number;
  total_copies: number;
  prompt_type: 'user' | 'admin';
  // Bilingual display fields supplied by backend
  title_display?: string;
  description_display?: string;
  full_text_display?: string;
  systemprompt_display?: string;
  language?: 'en' | 'es';
}

export interface PromptVersion {
  id: string;
  version_number: number;
  title: string;
  description: string;
  full_text: string;
  change_notes: string;
  created_at: string;
  created_by_name: string;
  systemprompt?: string;
  // Optional bilingual fields for versions
  title_es?: string;
  description_es?: string;
  full_text_es?: string;
  title_display?: string;
  description_display?: string;
  full_text_display?: string;
}

export const usePrompt = (id: string | undefined) => {
  const { i18n } = useLanguage();
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [latestVersion, setLatestVersion] = useState<PromptVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [latestSystemPrompt, setLatestSystemPrompt] = useState<string>('');
  const prevLanguageRef = useRef<string>(i18n.language);

  const fetchPromptDetails = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch base prompt details
      // Use authenticatedFetch so language (?lang=) and X-User-Language header are injected
      const response = await authenticatedFetch(API_URLS.PROMPTS.DETAIL(id));

      if (!response.ok) {
        if (response.status === 401) {
          // Surface unauthorized state; let consuming component decide UX (login modal, redirect, etc.)
          setError('unauthorized');
        } else if (response.status === 403) {
          setError('forbidden');
        } else if (response.status === 404) {
          setError('not_found');
        } else {
          setError('fetch_failed');
        }
        return;
      }

      const data = await response.json();
      // Handle both old direct response and new {success, data} format
      const promptData = data.success ? data.data : data;
      setPrompt(promptData);

      // Fetch latest version
      try {
        const versionsResponse = await authenticatedFetch(API_URLS.PROMPTS.VERSIONS.LIST(id));
        if (versionsResponse.ok) {
          const versionsData = await versionsResponse.json();
          // Handle both old direct array and new {success, data} format
          const versions = versionsData.success ? versionsData.data : versionsData;
          if (versions.length > 0) {
            const latest = versions[0];
            setLatestVersion(latest);
            setLatestSystemPrompt(latest.systemprompt || '');
          }
        }
      } catch (versionError) {
        console.warn('Failed to fetch latest version, using original:', versionError);
      }
    } catch (err) {
      console.error('Error fetching prompt details:', err instanceof Error ? err.message : 'Unknown error');
      setError('Failed to load prompt details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    let canceled = false;
    const doFetch = async () => {
      if (!isAuthReady()) {
        await waitForAuthReady();
      }
      if (!canceled) {
        fetchPromptDetails();
      }
    };
    doFetch();
    return () => {
      canceled = true;
    };
  }, [fetchPromptDetails]);

  // Detect language changes and refetch data
  useEffect(() => {
    if (prevLanguageRef.current !== i18n.language) {
      prevLanguageRef.current = i18n.language;
      fetchPromptDetails();
    }
  }, [i18n.language, fetchPromptDetails]);

  return { prompt, setPrompt, latestVersion, setLatestVersion, loading, setLoading, error, setError, latestSystemPrompt, setLatestSystemPrompt, fetchPromptDetails };
};
